/**
 * Mount de la pantalla `/logbook.html` — Fase 6b-UI-B.
 *
 * Flujo:
 *  1. `mountChrome({activeSection:'logbook'})` valida sesión y prepara
 *     el chrome militar reutilizable.
 *  2. Inyectamos el cuaderno (estructura HTML inicial) en `chrome-main`
 *     y reemplazamos la columna de acciones por la específica del logbook.
 *  3. Cargamos la lista de pilotos desde `GET /api/pilots`.
 *  4. Selección con click/Enter/Espacio. Doble click navega a la ficha.
 *     Con un piloto seleccionado, "VER FICHA" se habilita y navega
 *     a `/logbook/piloto.html?cs=<callsign>`.
 *
 * El chrome del proyecto sirve para todas las páginas autenticadas. La
 * página solo decide qué renderizar dentro de `chrome-main` y qué meter
 * en la columna de acciones.
 */

import type { PilotSummary } from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { fetchPilots } from './api.js';
import {
  buildPilotsFilters,
  defaultDirFor,
  renderActions,
  renderEmptyRow,
  renderErrorRow,
  renderHeaderRow,
  renderInfoPlaceholder,
  renderInfoPreview,
  renderListFiltersSidebar,
  renderLogbookListShell,
  renderPilotRow,
  sortPilots,
  type SortKey,
  type SortState,
} from './markup-list.js';
import { toggleFilters } from './panel-controls.js';

/**
 * Filtros aplicables al listado. Cadena vacía en enums = "sin filtro".
 * `null` en rangos numéricos = "sin límite por ese lado".
 */
interface ListFilters {
  rank: string;
  status: string;
  totalMissions: { min: number | null; max: number | null };
  totalKills: { min: number | null; max: number | null };
  totalKillsGround: { min: number | null; max: number | null };
  totalHoursDecimal: { min: number | null; max: number | null };
}

type RangeKey =
  | 'totalMissions'
  | 'totalKills'
  | 'totalKillsGround'
  | 'totalHoursDecimal';
const RANGE_KEYS: RangeKey[] = [
  'totalMissions',
  'totalKills',
  'totalKillsGround',
  'totalHoursDecimal',
];

export async function mountLogbookList(): Promise<void> {
  // Página pública: usuarios anónimos también pueden ver la lista de
  // pilotos. Es de solo lectura (no hay afford­ances de mutación aquí —
  // VER FICHA solo navega), así que basta con permitir el anónimo.
  const { contentRoot } = await mountChrome({
    activeSection: 'logbook',
    allowAnonymous: true,
  });

  // Estado de ordenación inicial: callsign ascendente (orden natural
  // de un "registro de pilotos").
  let sort: SortState = { key: 'callsign', dir: 'asc' };

  // Cuaderno + tabla vacía. Se rellena tras el fetch.
  contentRoot.innerHTML = renderLogbookListShell(sort);

  // Reemplazar columna de acciones del chrome por la del logbook, y
  // añadir debajo el sidebar de filtros (oculto hasta que pulsen el
  // botón FILTRAR).
  const actionsHost = document.querySelector<HTMLElement>(
    '.chrome-sidebar--right .chrome-actions',
  );
  if (actionsHost) {
    actionsHost.outerHTML = renderActions() + renderListFiltersSidebar();
  }

  const tbody = required<HTMLElement>(contentRoot, '#logbook-pilots');
  const infoEl = required<HTMLElement>(contentRoot, '#logbook-info');
  const headerRow = required<HTMLElement>(contentRoot, '.logbook-table-header');
  const viewBtn = document.querySelector<HTMLButtonElement>(
    '#logbook-action-view',
  );

  // Cargar pilotos.
  const result = await fetchPilots();
  if (!result.ok) {
    tbody.innerHTML = renderErrorRow(result.error);
    return;
  }
  const pilots = result.data;
  if (pilots.length === 0) {
    tbody.innerHTML = renderEmptyRow();
    return;
  }

  // Estado de selección.
  let activeCallsign: string | null = null;
  const byCallsign = new Map<string, PilotSummary>();
  for (const p of pilots) byCallsign.set(p.callsign, p);

  // Estado de filtros: cadena vacía / null = "sin filtro".
  const filters: ListFilters = {
    rank: '',
    status: '',
    totalMissions: { min: null, max: null },
    totalKills: { min: null, max: null },
    totalKillsGround: { min: null, max: null },
    totalHoursDecimal: { min: null, max: null },
  };

  // Inyectar la UI de filtros (selects + contador + LIMPIAR) en el
  // sidebar de filtros. Se hace una sola vez con los valores únicos
  // de la lista cargada; los selects se manipulan luego sin recrear
  // el HTML.
  const filtersContainer = document.querySelector<HTMLElement>(
    '[data-filters-sidebar] [data-filters]',
  );
  if (filtersContainer) {
    filtersContainer.innerHTML = buildPilotsFilters(pilots);
  }

  // Devuelve la lista filtrada según el estado actual de `filters`.
  const applyFilters = (list: PilotSummary[]): PilotSummary[] => {
    return list.filter((p) => {
      if (filters.rank && p.rank !== filters.rank) return false;
      if (filters.status && p.status !== filters.status) return false;
      for (const key of RANGE_KEYS) {
        const range = filters[key];
        const value = p[key];
        if (range.min !== null && value < range.min) return false;
        if (range.max !== null && value > range.max) return false;
      }
      return true;
    });
  };

  // Repinta cabecera + cuerpo + contador del sidebar.
  const renderRows = (): void => {
    const filtered = applyFilters(pilots);
    const sorted = sortPilots(filtered, sort);
    tbody.innerHTML = sorted
      .map((p) => renderPilotRow(p, p.callsign === activeCallsign))
      .join('');
    // Actualizar contador "MOSTRANDO X/Y".
    const countVisible = document.querySelector<HTMLElement>(
      '[data-filters-sidebar] [data-filters-visible]',
    );
    if (countVisible) countVisible.textContent = String(filtered.length);
  };
  renderRows();

  // Click en cabecera → cambiar ordenación. Misma columna invierte
  // la dirección; columna nueva entra con su dirección por defecto.
  headerRow.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>(
      '.logbook-th[data-sort-key]',
    );
    if (!btn) return;
    const key = btn.dataset['sortKey'] as SortKey | undefined;
    if (!key) return;
    if (sort.key === key) {
      sort = { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
    } else {
      sort = { key, dir: defaultDirFor(key) };
    }
    headerRow.innerHTML = renderHeaderRow(sort);
    renderRows();
  });

  // Botón FILTRAR del sidebar de acciones — toggle del sidebar de
  // filtros. Reusamos `toggleFilters` de panel-controls.ts.
  const filtersBtn = document.querySelector<HTMLButtonElement>(
    '#logbook-action-filters',
  );
  filtersBtn?.addEventListener('click', () => {
    if (filtersBtn) toggleFilters(filtersBtn);
  });

  // Cambio en cualquier select de filtro o input de rango → actualizar
  // estado + repintar. LIMPIAR resetea todos los campos al valor vacío.
  if (filtersContainer) {
    // Selects (enums RANGO + ESTADO) emiten `change`; los <input number>
    // emiten `input` para que el filtrado reaccione al teclear sin
    // tener que perder foco.
    filtersContainer.addEventListener('change', (ev) => {
      const sel = ev.target as HTMLSelectElement;
      if (sel.tagName !== 'SELECT') return;
      const col = sel.dataset['filterCol'];
      if (col === 'rank') filters.rank = sel.value;
      else if (col === 'status') filters.status = sel.value;
      else return;
      renderRows();
    });
    filtersContainer.addEventListener('input', (ev) => {
      const inp = ev.target as HTMLInputElement;
      if (inp.tagName !== 'INPUT') return;
      const col = inp.dataset['filterRange'] as RangeKey | undefined;
      const bound = inp.dataset['filterBound'];
      if (!col || (bound !== 'min' && bound !== 'max')) return;
      const raw = inp.value.trim();
      const parsed = raw === '' ? null : Number(raw);
      const next = parsed !== null && Number.isFinite(parsed) ? parsed : null;
      filters[col][bound] = next;
      renderRows();
    });
    filtersContainer.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLElement>(
        '[data-filter-action="clear"]',
      );
      if (!btn) return;
      filters.rank = '';
      filters.status = '';
      for (const key of RANGE_KEYS) {
        filters[key].min = null;
        filters[key].max = null;
      }
      filtersContainer
        .querySelectorAll<HTMLSelectElement>('select')
        .forEach((s) => (s.value = ''));
      filtersContainer
        .querySelectorAll<HTMLInputElement>('input[data-filter-range]')
        .forEach((i) => (i.value = ''));
      renderRows();
    });
  }

  function select(callsign: string): void {
    if (activeCallsign === callsign) return;

    // Desmarcar la fila anterior.
    if (activeCallsign !== null) {
      tbody
        .querySelector<HTMLLIElement>(
          `.logbook-row[data-callsign="${cssEscape(activeCallsign)}"]`,
        )
        ?.classList.remove('logbook-row--active');
    }

    // Marcar la nueva.
    activeCallsign = callsign;
    const newRow = tbody.querySelector<HTMLLIElement>(
      `.logbook-row[data-callsign="${cssEscape(callsign)}"]`,
    );
    newRow?.classList.add('logbook-row--active');

    // Preview en la página derecha.
    const pilot = byCallsign.get(callsign);
    if (pilot) {
      infoEl.dataset['state'] = 'preview';
      infoEl.innerHTML = renderInfoPreview(pilot);
    }

    // Habilitar VER FICHA.
    if (viewBtn) {
      viewBtn.disabled = false;
      viewBtn.setAttribute('aria-disabled', 'false');
    }
  }

  function openPilotDetail(callsign: string): void {
    window.location.href = `/logbook/piloto.html?cs=${encodeURIComponent(callsign)}`;
  }

  // Click en una fila → seleccionar; doble click → navegar.
  tbody.addEventListener('click', (ev) => {
    const row = (ev.target as HTMLElement).closest<HTMLLIElement>(
      '.logbook-row[data-callsign]',
    );
    if (!row) return;
    const callsign = row.dataset['callsign'];
    if (!callsign) return;
    select(callsign);
  });
  tbody.addEventListener('dblclick', (ev) => {
    const row = (ev.target as HTMLElement).closest<HTMLLIElement>(
      '.logbook-row[data-callsign]',
    );
    if (!row) return;
    const callsign = row.dataset['callsign'];
    if (!callsign) return;
    openPilotDetail(callsign);
  });

  // Accesibilidad: Enter/Espacio sobre la fila → seleccionar; Enter dos
  // veces seguidas (con la fila ya seleccionada) o Tab+Enter desde el
  // botón VER FICHA → navegar.
  tbody.addEventListener('keydown', (ev) => {
    const row = (ev.target as HTMLElement).closest<HTMLLIElement>(
      '.logbook-row[data-callsign]',
    );
    if (!row) return;
    const callsign = row.dataset['callsign'];
    if (!callsign) return;
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      if (activeCallsign === callsign && ev.key === 'Enter') {
        // Si ya estaba seleccionada y se pulsa Enter, navegar.
        openPilotDetail(callsign);
      } else {
        select(callsign);
      }
    }
  });

  // Botón VER FICHA.
  viewBtn?.addEventListener('click', () => {
    if (activeCallsign === null) return;
    openPilotDetail(activeCallsign);
  });

  // Reset de la página derecha cuando no hay selección — innecesario al
  // arrancar (ya empieza con placeholder), pero documentado por si en el
  // futuro añadimos un "deseleccionar".
  void renderInfoPlaceholder;
}

// ─── Helpers locales ──────────────────────────────────────────────────

function required<T extends HTMLElement>(
  root: ParentNode,
  selector: string,
): T {
  const el = root.querySelector<T>(selector);
  if (!el) {
    throw new Error(`[logbook] elemento requerido no encontrado: ${selector}`);
  }
  return el;
}

/**
 * `CSS.escape` no está en todos los entornos (jsdom de tests). Para
 * callsigns con dígitos/letras/`_` (lo que valida nuestro registro),
 * basta este escape mínimo. Si en el futuro permitimos `"`, `\`, etc.
 * sustituiremos por `CSS.escape`.
 */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
